import { trpc } from "@/client/trpc";
import { buildMeaningHintPrompt } from "@/util/prompts/meaningHint";
import { useEffect, useRef, useState } from "react";
import { ScrollView } from "react-native";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import { AiPromptPreview } from "./AiPromptPreview";
import { PageSheetModal } from "./PageSheetModal";
import { Pylymark } from "./Pylymark";
import { RectButton } from "./RectButton";
import { buildMeaningHintCausualBridgePrompt } from "@/util/prompts/meaningHintCausualBridge";
import { buildMeaningHintLogicalPrompt } from "@/util/prompts/meaningHintLogical";

export interface AiMeaningHintModalProps {
  hanzi: string;
  meaning: { hanziWord: string; glosses: readonly string[] };
  components: readonly MeaningHintComponent[];
  onApplyHint: (hint: { text: string; explanation?: string | null }) => void;
  onDismiss: () => void;
}

export interface MeaningHintComponent {
  hanzi?: string;
  label?: string;
  meaning?: string;
}

type HintSuggestion = {
  hint: string;
  explanation?: string | null;
  strategyLabel: string;
};

export function AiMeaningHintModal({
  hanzi,
  meaning,
  components,
  onApplyHint,
  onDismiss,
}: AiMeaningHintModalProps) {
  const [suggestions, setSuggestions] = useState<HintSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSettledGeneration, setHasSettledGeneration] = useState(false);
  const hasAutoGeneratedRef = useRef(false);

  const generateMutation = trpc.ai.generateMeaningHints.useMutation();
  const initialRequestInputRef = useRef({
    hanzi,
    meaning: {
      hanziWord: meaning.hanziWord,
      glosses: [...meaning.glosses],
    },
    components: components.map((component) => ({ ...component })),
    count: 4,
  });

  const visualPrompt = buildMeaningHintPrompt({
    hanzi,
    meaning: {
      hanziWord: meaning.hanziWord,
      glosses: [...meaning.glosses],
    },
    components: components.map((component) => ({ ...component })),
    count: 4,
  });

  const logicalPrompt = buildMeaningHintLogicalPrompt({
    hanzi,
    meaning: {
      hanziWord: meaning.hanziWord,
      glosses: [...meaning.glosses],
    },
    components: components.map((component) => ({ ...component })),
    count: 4,
  });

  const causalBridgePrompt = buildMeaningHintCausualBridgePrompt({
    hanzi,
    meaning: {
      hanziWord: meaning.hanziWord,
      glosses: [...meaning.glosses],
    },
    components: components.map((component) => ({ ...component })),
    count: 4,
  });

  const handleGenerate = async () => {
    setError(null);
    setHasSettledGeneration(false);

    try {
      const result = await generateMutation.mutateAsync({
        hanzi,
        meaning: {
          hanziWord: meaning.hanziWord,
          glosses: [...meaning.glosses],
        },
        components: components.map((component) => ({ ...component })),
        count: 4,
      });
      setSuggestions((prevSuggestions) => [
        ...(prevSuggestions ?? []),
        ...result.suggestions,
      ]);
    } catch (err) {
      console.error(`AI meaning hint generation failed:`, err);
      setError(`Unable to generate hints right now.`);
    } finally {
      setHasSettledGeneration(true);
    }
  };

  const isGenerating = generateMutation.isPending;

  useEffect(() => {
    if (hasAutoGeneratedRef.current) {
      return;
    }

    hasAutoGeneratedRef.current = true;
    setError(null);
    setHasSettledGeneration(false);

    void generateMutation
      .mutateAsync(initialRequestInputRef.current)
      .then((result) => {
        setSuggestions(result.suggestions);
      })
      .catch((err: unknown) => {
        console.error(`AI meaning hint generation failed:`, err);
        setError(`Unable to generate hints right now.`);
      })
      .finally(() => {
        setHasSettledGeneration(true);
      });
  }, [generateMutation]);

  return (
    <PageSheetModal
      onDismiss={onDismiss}
      suspenseFallback={<Text>Loading...</Text>}
    >
      {({ dismiss }) => (
        <View className="flex-1 bg-bg">
          <View className="flex-row items-center justify-between border-b border-fg/10 px-4 py-3">
            <RectButton variant="bare" onPress={dismiss}>
              Cancel
            </RectButton>
            <Text className="font-sans text-[17px] font-semibold text-fg-loud">
              AI meaning hint creator
            </Text>
            <View className="w-18" />
          </View>

          <ScrollView className="flex-1" contentContainerClassName="gap-4 p-4">
            {error == null ? null : (
              <Text className="font-sans text-[14px] text-[crimson]">
                {error}
              </Text>
            )}

            <View className="gap-2">
              <Text className="pyly-body-subheading">Suggestions</Text>
              {!hasSettledGeneration || isGenerating ? (
                <Text className="font-sans text-[14px] text-muted-fg">
                  Generating hints...
                </Text>
              ) : null}

              {suggestions == null ? (
                error == null && hasSettledGeneration ? (
                  <Text className="font-sans text-[14px] text-muted-fg">
                    No hints generated.
                  </Text>
                ) : null
              ) : (
                <View className="gap-3">
                  {suggestions.map((suggestion, index) => (
                    <View
                      key={`${index}-${suggestion.hint}`}
                      className="gap-2 rounded-lg border border-fg-bg10 bg-fg-bg5 p-3"
                    >
                      <View className="flex-row items-center justify-between">
                        <Text
                          className={`
                            rounded bg-fg-bg10 px-2 py-0.5 font-sans text-[11px] font-semibold
                            tracking-wide text-muted-fg uppercase
                          `}
                        >
                          {suggestion.strategyLabel}
                        </Text>
                        <RectButton
                          variant="bare"
                          onPress={() => {
                            onApplyHint({
                              text: suggestion.hint,
                              explanation: suggestion.explanation ?? null,
                            });
                            dismiss();
                          }}
                        >
                          Use hint
                        </RectButton>
                      </View>
                      <Text className="pyly-body">
                        <Pylymark source={suggestion.hint} />
                      </Text>
                      {suggestion.explanation == null ? null : (
                        <Text className="font-sans text-[13px] text-muted-fg">
                          <Pylymark source={suggestion.explanation} />
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              )}

              {hasSettledGeneration && !isGenerating ? (
                <RectButton
                  variant="bareDim"
                  onPress={() => {
                    void handleGenerate();
                  }}
                >
                  Generate more
                </RectButton>
              ) : null}
            </View>

            <AiPromptPreview
              description="Prompt text generated from the same builders used by AI hint generation."
              sections={[
                {
                  title: `Visual`,
                  messages: visualPrompt.messages,
                },
                {
                  title: `Logical`,
                  messages: logicalPrompt.messages,
                },
                {
                  title: `Causal Bridge`,
                  messages: causalBridgePrompt.messages,
                },
              ]}
            />
          </ScrollView>
        </View>
      )}
    </PageSheetModal>
  );
}
